import React, {
  useCallback, useMemo, useState,
} from 'react';
import {
  AlertTriangle,
  CheckCircle,
  CircleDashed,
  CircleSlash,
  Info,
  RotateCcw,
} from 'lucide-react-native';
import {
  Box,
  Button,
  ButtonSpinner,
  ButtonText,
  HStack,
  Icon,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import {
  useCancelOralExamBookingAllowRebooking,
  useCloseOralExamWrapUp,
  useSubmitOralExamBookingForEvaluation,
} from '@/common/api/endpoints/oral-exams/oral-exams';
import { getGetLecturerSlotsQueryKey } from '@/common/api/endpoints/appointments/appointments';
import { getOralExamApiErrorMessage } from '@/common/hooks/oralExam/oralExamApiError';
import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';
import { useDialog } from '@/common/context/DialogContext';
import { useOralExamCohortState } from '@/common/hooks/oralExam/useOralExamCohortState';
import { SegmentedPill } from '@/lecturer/ModuleEditor/ModuleMetaEditor/ModuleMetaForm/components/SegmentedPill';

type RowClassification = 'clean_submitted' | 'recording_missing' | 'no_show' | 'not_completed' | 'voided';
type RowResolution = 'submitted' | 'cancelled';
type WrapUpResolutionChoice = 'submit' | 'cancel';
type PendingAction = 'submit' | 'cancel';

function ifMatchHeaders(etag: string | null | undefined): { 'If-Match': string } | undefined {
  if (!etag) return undefined;
  return { 'If-Match': etag };
}

export function classifyCohortWrapUpSummary(summary: OralExamBookingRunSummary): RowClassification {
  const { state } = summary.runState;
  if (state === 'completed') {
    if (summary.runState.recordingRequired === false) return 'clean_submitted';
    if (summary.hasRecording === false) return 'recording_missing';
    return 'clean_submitted';
  }
  if (state === 'pre_start') return 'no_show';
  if (state === 'aborted' && summary.runState.endReason === 'session_void') return 'voided';
  if (state === 'aborted' || state === 'in_progress') return 'not_completed';
  return 'not_completed';
}

export function defaultWrapUpResolution(classification: RowClassification): WrapUpResolutionChoice {
  if (classification === 'no_show') return 'cancel';
  return 'submit';
}

function isAmbiguous(classification: RowClassification): boolean {
  return classification !== 'clean_submitted' && classification !== 'voided';
}

type OutcomeRowProps = {
  summary: OralExamBookingRunSummary;
  classification: RowClassification;
  resolution: RowResolution | null;
  choice: WrapUpResolutionChoice;
  pendingAction: PendingAction | null;
  hasError: boolean;
  onChoiceChange: (choice: WrapUpResolutionChoice) => void;
  sendLabel: string;
  cancelLabel: string;
};

function OutcomeRow({
  summary,
  classification,
  resolution,
  choice,
  pendingAction,
  hasError,
  onChoiceChange,
  sendLabel,
  cancelLabel,
}: OutcomeRowProps) {
  const { t } = useTranslation();
  const { bookingId, studentName, studentId } = summary;
  const name = studentName ?? studentId;
  const isPending = pendingAction !== null;
  const effectiveResolution = classification === 'clean_submitted' ? 'submitted' : resolution;

  const leadingIcon = (() => {
    if (effectiveResolution === 'submitted') {
      return { IconComp: CheckCircle, color: '$statusSuccess' as const };
    }
    if (effectiveResolution === 'cancelled') {
      return { IconComp: RotateCcw, color: '$textSubtle' as const };
    }
    if (classification === 'recording_missing') {
      return { IconComp: AlertTriangle, color: '$statusWarning' as const };
    }
    if (classification === 'no_show') {
      return { IconComp: CircleDashed, color: '$textSubtle' as const };
    }
    if (classification === 'voided') {
      return { IconComp: CircleSlash, color: '$textSubtle' as const };
    }
    return { IconComp: CircleSlash, color: '$textSubtle' as const };
  })();

  const subtext = (() => {
    if (
      effectiveResolution === 'submitted'
      || classification === 'clean_submitted'
      || classification === 'voided'
    ) {
      return null;
    }
    if (effectiveResolution === 'cancelled') {
      return null;
    }
    if (classification === 'recording_missing') {
      return t('oralExam.wrapUp.cohort.recordingMissing', {
        defaultValue: 'Submitted · recording missing',
      });
    }
    if (classification === 'no_show') {
      return t('oralExam.wrapUp.cohort.noShow', {
        defaultValue: 'No show — never joined',
      });
    }
    return t('oralExam.wrapUp.cohort.notCompleted', {
      defaultValue: 'Not completed (ended early)',
    });
  })();

  const subtextColor = classification === 'recording_missing' && !effectiveResolution
    ? '$statusWarning'
    : '$textSubtle';

  const trailing = (() => {
    if (effectiveResolution === 'submitted') {
      return (
        <HStack space="xs" alignItems="center" testID={`cohort-wrapup-resolved-submitted-${bookingId}`}>
          <Icon as={CheckCircle} size="xs" color="$statusSuccess" />
          <Text size="sm" color="$textSubtle">
            {t('oralExam.wrapUp.cohort.sentToGrading', { defaultValue: 'Sent to grading' })}
          </Text>
        </HStack>
      );
    }
    if (effectiveResolution === 'cancelled') {
      return (
        <HStack space="xs" alignItems="center" testID={`cohort-wrapup-resolved-cancelled-${bookingId}`}>
          <Icon as={RotateCcw} size="xs" color="$textSubtle" />
          <Text size="sm" color="$textSubtle">
            {t('oralExam.wrapUp.cohort.cancelledRebook', {
              defaultValue: 'Cancelled — student can rebook',
            })}
          </Text>
        </HStack>
      );
    }
    if (classification === 'voided') {
      return (
        <Text size="sm" color="$textSubtle" testID={`cohort-wrapup-voided-${bookingId}`}>
          {t('oralExam.wrapUp.cohort.voidedNotGraded', {
            defaultValue: 'Voided — not graded',
          })}
        </Text>
      );
    }

    return (
      <VStack
        space="xs"
        alignItems="flex-end"
        minWidth={220}
        testID={isPending ? `cohort-wrapup-pending-${bookingId}` : undefined}
      >
        <SegmentedPill
          testID={`cohort-wrapup-pill-${bookingId}`}
          value={choice}
          onChange={(value) => onChoiceChange(value as WrapUpResolutionChoice)}
          disabled={isPending}
          options={[
            {
              label: sendLabel,
              value: 'submit',
              testID: `cohort-wrapup-pill-${bookingId}-submit`,
            },
            {
              label: cancelLabel,
              value: 'cancel',
              testID: `cohort-wrapup-pill-${bookingId}-cancel`,
            },
          ]}
        />
        {hasError ? (
          <Text size="xs" color="$statusError" testID={`cohort-wrapup-row-error-${bookingId}`}>
            {t('oralExam.wrapUp.cohort.rowError', {
              defaultValue: "Couldn't save — try again.",
            })}
          </Text>
        ) : null}
      </VStack>
    );
  })();

  return (
    <HStack
      px="$4"
      py="$3"
      alignItems="flex-start"
      justifyContent="space-between"
      flexWrap="wrap"
      space="md"
      testID={`cohort-wrapup-row-${bookingId}`}
      accessibilityLiveRegion={effectiveResolution ? 'polite' : undefined}
    >
      <HStack space="md" alignItems="flex-start" flexShrink={1} flexGrow={1}>
        <Icon as={leadingIcon.IconComp} size="md" color={leadingIcon.color} mt="$0.5" />
        <VStack flexShrink={1}>
          <Text size="md" fontWeight="semibold" color="$textHeading">
            {name}
          </Text>
          {subtext ? (
            <Text size="sm" color={subtextColor}>
              {subtext}
            </Text>
          ) : null}
        </VStack>
      </HStack>
      <Box flexShrink={0} alignSelf="center">
        {trailing}
      </Box>
    </HStack>
  );
}

export type LecturerCohortWrapUpProps = {
  slotId: string;
  bookingRuns: OralExamBookingRunSummary[];
  onGoToEvaluationDesk?: () => void;
  onClose: () => void;
};

/**
 * Outcome-resolution wrap-up shown when cohort sessionPhase === post_exam (§3.9 v0.8).
 */
export function LecturerCohortWrapUp({
  slotId,
  bookingRuns,
  onGoToEvaluationDesk,
  onClose,
}: LecturerCohortWrapUpProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showDialog, showToastMessage } = useDialog();
  const { queryKey, etag } = useOralExamCohortState(slotId, { polling: true });

  const [resolutions, setResolutions] = useState<Record<string, RowResolution>>({});
  const [choices, setChoices] = useState<Record<string, WrapUpResolutionChoice>>({});
  const [pending, setPending] = useState<Record<string, PendingAction>>({});
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [batchPending, setBatchPending] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closePending, setClosePending] = useState(false);

  const sendLabel = t('oralExam.wrapUp.cohort.sendToGrading', { defaultValue: 'Send to grading' });
  const cancelLabel = t('oralExam.wrapUp.cohort.cancelAndRebookShort', {
    defaultValue: 'Cancel & rebook',
  });

  const invalidateCohort = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const onMutationError = useCallback((bookingId: string, error: unknown) => {
    const status = error instanceof AxiosError ? error.response?.status : undefined;
    if (status === 412) {
      invalidateCohort();
    }
    setPending((prev) => {
      const next = { ...prev };
      delete next[bookingId];
      return next;
    });
    setErrors((prev) => ({ ...prev, [bookingId]: true }));
  }, [invalidateCohort]);

  const submitMutation = useSubmitOralExamBookingForEvaluation({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateCohort();
        setResolutions((prev) => ({ ...prev, [variables.bookingId]: 'submitted' }));
        setPending((prev) => {
          const next = { ...prev };
          delete next[variables.bookingId];
          return next;
        });
        setErrors((prev) => ({ ...prev, [variables.bookingId]: false }));
      },
      onError: (error, variables) => {
        onMutationError(variables.bookingId, error);
      },
    },
  });

  const closeWrapUpMutation = useCloseOralExamWrapUp();

  const dismissWrapUp = useCallback(async () => {
    setCloseError(null);
    setClosePending(true);
    try {
      await closeWrapUpMutation.mutateAsync({
        slotId,
        headers: ifMatchHeaders(etag),
      });
      invalidateCohort();
      queryClient.invalidateQueries({ queryKey: getGetLecturerSlotsQueryKey() });
      onClose();
    } catch (error) {
      const status = error instanceof AxiosError ? error.response?.status : undefined;
      if (status === 412) {
        invalidateCohort();
        const staleMessage = t('oralExam.staleRetry', {
          defaultValue: 'The session changed — please try ending the exam again.',
        });
        setCloseError(staleMessage);
        showToastMessage('info', staleMessage);
        return;
      }
      const message = getOralExamApiErrorMessage(error, t('oralExam.actionFailed'));
      setCloseError(message);
    } finally {
      setClosePending(false);
    }
  }, [
    closeWrapUpMutation,
    etag,
    invalidateCohort,
    onClose,
    queryClient,
    showToastMessage,
    slotId,
    t,
  ]);

  const cancelMutation = useCancelOralExamBookingAllowRebooking({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateCohort();
        setResolutions((prev) => ({ ...prev, [variables.bookingId]: 'cancelled' }));
        setPending((prev) => {
          const next = { ...prev };
          delete next[variables.bookingId];
          return next;
        });
        setErrors((prev) => ({ ...prev, [variables.bookingId]: false }));
      },
      onError: (error, variables) => {
        onMutationError(variables.bookingId, error);
      },
    },
  });

  const rows = useMemo(
    () => bookingRuns.map((summary) => ({
      summary,
      classification: classifyCohortWrapUpSummary(summary),
    })),
    [bookingRuns],
  );

  const getChoice = useCallback((
    bookingId: string,
    classification: RowClassification,
  ): WrapUpResolutionChoice => (
    choices[bookingId] ?? defaultWrapUpResolution(classification)
  ), [choices]);

  const unresolvedRows = useMemo(
    () => rows.filter(({ summary, classification }) => {
      const resolution = resolutions[summary.bookingId] ?? null;
      return isAmbiguous(classification) && resolution !== 'submitted' && resolution !== 'cancelled';
    }),
    [rows, resolutions],
  );

  const applyRows = useCallback(async (
    targetRows: typeof rows,
    resolveChoice: (bookingId: string, classification: RowClassification) => WrapUpResolutionChoice,
  ): Promise<boolean> => {
    if (targetRows.length === 0) return true;

    setBatchPending(true);
    try {
      const results = await Promise.allSettled(targetRows.map(async ({ summary, classification }) => {
        const { bookingId } = summary;
        const choice = resolveChoice(bookingId, classification);
        setErrors((prev) => ({ ...prev, [bookingId]: false }));
        setPending((prev) => ({ ...prev, [bookingId]: choice }));
        const payload = {
          courseId: summary.courseId,
          moduleId: summary.moduleId,
          taskId: summary.taskId,
          bookingId: summary.bookingId,
          headers: ifMatchHeaders(summary.runState.etag),
        };
        if (choice === 'submit') {
          await submitMutation.mutateAsync(payload);
        } else {
          await cancelMutation.mutateAsync(payload);
        }
      }));
      return results.every((result) => result.status === 'fulfilled');
    } finally {
      setBatchPending(false);
    }
  }, [cancelMutation, submitMutation]);

  const runWithCancelConfirm = useCallback((
    targetRows: typeof rows,
    resolveChoice: (bookingId: string, classification: RowClassification) => WrapUpResolutionChoice,
    onComplete: () => void,
  ): Promise<void> | undefined => {
    const hasCancel = targetRows.some(
      ({ summary, classification }) => resolveChoice(summary.bookingId, classification) === 'cancel',
    );

    const execute = async () => {
      const allSucceeded = await applyRows(targetRows, resolveChoice);
      if (allSucceeded) {
        onComplete();
      }
    };

    if (!hasCancel) {
      return execute();
    }

    showDialog({
      title: t('oralExam.wrapUp.cohort.batchCancelDialogTitle', {
        defaultValue: 'Cancel & allow re-booking',
      }),
      message: t('oralExam.wrapUp.cohort.batchCancelDialogBody', {
        defaultValue:
          'Some outcomes will be cancelled so students can re-book. They will be notified. Continue?',
      }),
      buttons: [
        {
          text: t('common.buttons.cancel', { defaultValue: 'Cancel' }),
          variant: 'outline',
          action: 'secondary',
          testID: 'cohort-wrapup-cancel-dialog-dismiss',
        },
        {
          text: t('oralExam.wrapUp.cohort.cancelDialogConfirm', { defaultValue: 'Continue' }),
          action: 'secondary',
          testID: 'cohort-wrapup-cancel-dialog-confirm',
          onPress: async () => {
            await execute();
          },
        },
      ],
    });
    return undefined;
  }, [applyRows, showDialog, t]);

  const handleConfirmOutcomes = useCallback(async () => {
    await runWithCancelConfirm(
      unresolvedRows,
      (bookingId, classification) => getChoice(bookingId, classification),
      () => undefined,
    );
  }, [getChoice, runWithCancelConfirm, unresolvedRows]);

  const handleClose = useCallback(async () => {
    if (unresolvedRows.length === 0) {
      await dismissWrapUp();
      return;
    }

    await runWithCancelConfirm(
      unresolvedRows,
      (_bookingId, classification) => defaultWrapUpResolution(classification),
      dismissWrapUp,
    );
  }, [dismissWrapUp, runWithCancelConfirm, unresolvedRows]);

  const handleChoiceChange = useCallback((bookingId: string, choice: WrapUpResolutionChoice) => {
    setChoices((prev) => ({ ...prev, [bookingId]: choice }));
    setErrors((prev) => ({ ...prev, [bookingId]: false }));
  }, []);

  const { cleanSubmitted, unresolvedAmbiguous, voided } = useMemo(() => {
    let clean = 0;
    let unresolved = 0;
    let voidedCount = 0;
    rows.forEach(({ summary, classification }) => {
      const resolution = resolutions[summary.bookingId] ?? null;
      if (classification === 'voided') {
        voidedCount += 1;
      } else if (classification === 'clean_submitted' || resolution === 'submitted') {
        clean += 1;
      } else if (isAmbiguous(classification) && resolution !== 'cancelled') {
        unresolved += 1;
      }
    });
    return { cleanSubmitted: clean, unresolvedAmbiguous: unresolved, voided: voidedCount };
  }, [rows, resolutions]);

  const n = bookingRuns.length;
  const isEmpty = n === 0;
  const gradable = n - voided;

  return (
    <VStack
      flex={1}
      p="$5"
      justifyContent="center"
      alignItems="center"
      testID="lecturer-cohort-wrapup"
    >
      <VStack space="lg" maxWidth={680} width="100%" alignItems="center">
        <VStack space="sm" alignItems="center" accessibilityLiveRegion="polite">
          <Box
            w={48}
            h={48}
            borderRadius="$full"
            borderWidth={2}
            borderColor="$statusSuccess"
            alignItems="center"
            justifyContent="center"
            testID="cohort-wrapup-checkmark"
          >
            <Icon as={CheckCircle} size="md" color="$statusSuccess" />
          </Box>
          <Text
            size="2xl"
            fontWeight="bold"
            color="$textHeading"
            textAlign="center"
            testID="cohort-wrapup-title"
          >
            {isEmpty || gradable === 0
              ? t('oralExam.wrapUp.cohort.titleEmpty', { defaultValue: 'Exam ended' })
              : t('oralExam.wrapUp.cohort.titleComplete', { defaultValue: 'Exam complete' })}
          </Text>
          {isEmpty ? (
            <Text size="md" color="$textBody" textAlign="center">
              {t('oralExam.wrapUp.cohort.emptyBody', {
                defaultValue: 'No students were in this exam.',
              })}
            </Text>
          ) : (
            <>
              {gradable > 0 ? (
                <Text
                  size="md"
                  color="$textBody"
                  textAlign="center"
                  testID="cohort-wrapup-counts"
                >
                  {t('oralExam.wrapUp.cohort.countLine', {
                    clean: cleanSubmitted,
                    total: gradable,
                    defaultValue: '{{clean}} of {{total}} sent to grading automatically',
                  })}
                </Text>
              ) : null}
              {voided > 0 ? (
                <Text
                  size="md"
                  color="$textBody"
                  textAlign="center"
                  testID="cohort-wrapup-voided-count"
                >
                  {t('oralExam.wrapUp.cohort.voidedCountLine', {
                    voided,
                    defaultValue: '{{voided}} voided — not graded',
                  })}
                </Text>
              ) : null}
              {unresolvedAmbiguous > 0 ? (
                <Text
                  size="sm"
                  color="$statusWarning"
                  textAlign="center"
                  testID="cohort-wrapup-needs-decision"
                >
                  {t('oralExam.wrapUp.cohort.needsDecision', {
                    count: unresolvedAmbiguous,
                    defaultValue: '{{count}} outcome(s) need your decision below',
                  })}
                </Text>
              ) : null}
            </>
          )}
        </VStack>

        {!isEmpty ? (
          <Box
            width="100%"
            borderWidth={1}
            borderColor="$borderLight"
            borderRadius="$lg"
            overflow="hidden"
            testID="cohort-wrapup-student-list"
          >
            {rows.map(({ summary, classification }, idx) => (
              <Box key={summary.bookingId}>
                {idx > 0 ? (
                  <Box height={1} bg="$borderLight" />
                ) : null}
                <OutcomeRow
                  summary={summary}
                  classification={classification}
                  resolution={resolutions[summary.bookingId] ?? null}
                  choice={getChoice(summary.bookingId, classification)}
                  pendingAction={pending[summary.bookingId] ?? null}
                  hasError={errors[summary.bookingId] ?? false}
                  onChoiceChange={(choice) => handleChoiceChange(summary.bookingId, choice)}
                  sendLabel={sendLabel}
                  cancelLabel={cancelLabel}
                />
              </Box>
            ))}
          </Box>
        ) : null}

        {unresolvedAmbiguous > 0 ? (
          <>
            <HStack space="sm" alignItems="flex-start" testID="cohort-wrapup-reconciliation-note">
              <Icon as={Info} size="xs" color="$textSubtle" mt="$0.5" />
              <Text size="sm" color="$textSubtle" flex={1}>
                {t('oralExam.wrapUp.cohort.reconciliationNote', {
                  defaultValue:
                    'You can leave anytime. Unresolved outcomes follow the defaults above — sat students go to grading, no-shows are cancelled for re-booking. Use Confirm outcomes to apply your selections.',
                })}
              </Text>
            </HStack>
            <Button
              size="lg"
              action="primary"
              onPress={handleConfirmOutcomes}
              isDisabled={batchPending}
              testID="cohort-wrapup-confirm"
            >
              {batchPending ? <ButtonSpinner mr="$1" /> : null}
              <ButtonText>
                {t('oralExam.wrapUp.cohort.confirmOutcomes', { defaultValue: 'Confirm outcomes' })}
              </ButtonText>
            </Button>
          </>
        ) : null}

        {closeError ? (
          <Text size="sm" color="$error600" testID="cohort-wrapup-close-error">
            {closeError}
          </Text>
        ) : null}

        <HStack space="md" justifyContent="center" flexWrap="wrap">
          {onGoToEvaluationDesk ? (
            <Button
              size="lg"
              action="primary"
              onPress={onGoToEvaluationDesk}
              testID="cohort-wrapup-evaluation-desk"
            >
              <ButtonText>
                {t('oralExam.wrapUp.cohort.goToEvaluationDesk', {
                  defaultValue: 'Go to Evaluations',
                })}
              </ButtonText>
            </Button>
          ) : null}
          <Button
            size="lg"
            variant={onGoToEvaluationDesk ? 'outline' : 'solid'}
            action={onGoToEvaluationDesk ? 'secondary' : 'primary'}
            onPress={handleClose}
            isDisabled={batchPending || closePending}
            testID="cohort-wrapup-close"
          >
            {closePending ? <ButtonSpinner mr="$1" /> : null}
            <ButtonText>
              {t('oralExam.wrapUp.cohort.backToSchedule', { defaultValue: 'Back to schedule' })}
            </ButtonText>
          </Button>
        </HStack>
      </VStack>
    </VStack>
  );
}
